import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';

import styled from 'styled-components';

import { Breakpoint, Button, ContextMenu } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';

import ShareModal from './modals/ShareModal';
import SignModal from './modals/SignModal';
import { ArchiveIcon, ChevronDownIcon, PrintIcon } from '@treams/treams-icons';

const Footer = styled(Box)`
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
	position: fixed;
	bottom: 0;
	right: 0;
	left: 0;
	background: #fff;
	padding-right: 100px;
	padding-left: 16px;
	padding-top: 8px;
	padding-bottom: 8px;
	display: flex;
	justify-content: space-between;
	@media screen and (min-width: ${Breakpoint.Medium}) {
		left: 270px;
	}
`;

const Actions = styled(Box)`
	display: flex;
	gap: 8px;
	flex-direction: row;
`;

function ConversationContextMenu({ conversation }: { conversation: graphql.Conversation }) {
	const { t } = useTranslation();

	const [archive, { loading: archiveLoading }] = graphql.useArchiveConversationMutation({
		refetchQueries: ['Conversation'],
		variables: { conversationID: conversation.id },
	});

	const [restore, { loading: restoreLoading }] = graphql.useRestoreConversationMutation({
		refetchQueries: ['Conversation'],
		variables: { conversationID: conversation.id },
	});

	return (
		<ContextMenu.Target
			menu={
				<ContextMenu width={260}>
					{conversation.viewerCanArchive && (
						<>
							<a href={`${location.pathname}/print`} target="_blank" rel="noreferrer">
								<ContextMenu.Item title={t('evaluation.submission.print')} icon={<PrintIcon />} />
							</a>
							<ContextMenu.Separator />
							<ContextMenu.Item
								title={
									archiveLoading
										? t('evaluation.submission.archiveRestore.archiving')
										: restoreLoading
										? t('evaluation.submission.archiveRestore.restoring')
										: conversation.isArchived
										? t('evaluation.submission.archiveRestore.restore')
										: t('evaluation.submission.archiveRestore.archive')
								}
								icon={<ArchiveIcon />}
								type="destructive"
								onClick={async () => {
									conversation.isArchived ? await restore() : await archive();
								}}
							/>
						</>
					)}
				</ContextMenu>
			}
		>
			<Flex flexShrink={0}>
				<Button
					small
					title={t('evaluation.submission.actions.button')}
					mr={2}
					type="secondary"
					rightIcon={<ChevronDownIcon />}
				/>
			</Flex>
		</ContextMenu.Target>
	);
}

export default function ConversationFooter(props: { conversation: graphql.Conversation }) {
	const { t } = useTranslation();
	const [modal, setModal] = useState<'share' | 'sign' | 'partner' | null>(null);

	const handleDismiss = () => {
		setModal(null);
		window.scrollTo(0, 0);
	};

	const [unsignConversationMutation, { loading: unsigning }] =
		graphql.useUnsignConversationMutation({
			refetchQueries: ['Conversation'],
		});

	return (
		<>
			<ShareModal
				conversationID={props.conversation.id}
				opened={modal === 'share'}
				onDismiss={handleDismiss}
				onShare={handleDismiss}
			/>

			<SignModal
				conversationID={props.conversation.id}
				opened={modal === 'sign'}
				onDismiss={handleDismiss}
			/>

			<Footer py={2}>
				<div></div>

				<Actions>
					<ConversationContextMenu conversation={props.conversation} />

					{props.conversation.viewerCanSign && (
						<Button
							small
							title={t('conversations.confirm.reminder.button')}
							onClick={() => setModal('sign')}
						/>
					)}

					{props.conversation.viewerCanUnsign && (
						<Button
							small
							type="secondary"
							title={t('evaluation.submission.save.unsign')}
							onClick={() => {
								unsignConversationMutation({
									variables: {
										conversationID: props.conversation.id,
									},
								});
							}}
							loading={unsigning}
						/>
					)}

					{props.conversation.viewerCanShare && (
						<Button
							small
							title={t('evaluation.submission.save.share')}
							onClick={() => setModal('share')}
						/>
					)}
				</Actions>
			</Footer>
		</>
	);
}
